import Constants from "../constants";
import Fund from "../fund";
import FundAllocation from "../fundAllocation";
import fundInfo from "../funds";
import FundAllocationCalculation from "./fundAllocationCalculation";

export default class FundAllocationCalculator {

    public static calculate(pensionPotsTotalValue: number, pensionPotDrawdownAmounts: Array<number>, isAlreadyRetired: boolean, isTakingLumpSum: boolean, lumpSumHoldingFund: Fund, 
        numberOfYearsHeldInMatchingIncome: number): FundAllocationCalculation {
        let trusteeAccountPercentage = 1;
        let moneyFundPercentage: number = FundAllocationCalculator.getMoneyFundPercentage(isAlreadyRetired, isTakingLumpSum, lumpSumHoldingFund);
        let incomeFundAmount = 0;
    
        if (numberOfYearsHeldInMatchingIncome > 0)
        {
            for (let matchedYear = 0; matchedYear < numberOfYearsHeldInMatchingIncome; matchedYear++) {
                incomeFundAmount += pensionPotDrawdownAmounts[matchedYear];
            }
        }

        const incomeFundPercentage = Math.round((incomeFundAmount / pensionPotsTotalValue) * 1000) / 10;
        const growthFundPercentage = 100 - trusteeAccountPercentage - moneyFundPercentage - incomeFundPercentage;
    
        const percentageForLumpSumCalc = isTakingLumpSum ? Constants.TaxFreeLumpSumPercentage : 0;
        const taxFreeLumpSumAmount = FundAllocationCalculator.calcAmountFromPercentage(pensionPotsTotalValue, percentageForLumpSumCalc);

        const allocations = new Array<FundAllocation>();

        allocations.push(FundAllocationCalculator.createFundAllocation(fundInfo.trusteeBankAccount, trusteeAccountPercentage, pensionPotsTotalValue));
        
        allocations.push(FundAllocationCalculator.createFundAllocation(fundInfo.moneyFund, moneyFundPercentage, pensionPotsTotalValue));
        
        const matchingIncomeFundAlloc = new FundAllocation(fundInfo.matchingIncomeFund, incomeFundPercentage, incomeFundAmount);
        allocations.push(matchingIncomeFundAlloc);
        
        const alreadyAllocated = allocations.reduce((sum, a) => sum + a.amount, 0);
        const remainingForAllocation = pensionPotsTotalValue - alreadyAllocated;
        const growthFundAlloc = new FundAllocation(fundInfo.responsibleGrowthFund, growthFundPercentage, remainingForAllocation);
        allocations.push(growthFundAlloc);

        return new FundAllocationCalculation(allocations, taxFreeLumpSumAmount);
    }

    private static createFundAllocation(fund: Fund, percentage: number, total: number): FundAllocation {
        const amount = FundAllocationCalculator.calcAmountFromPercentage(total, percentage);
        return new FundAllocation(fund, percentage, amount);
    }
    
    private static calcAmountFromPercentage(total: number, percentage: number): number {
        if (percentage < 0 || percentage > 100) {
            throw new Error("Percentage must be between 0 and 100");
        }
    
        const decimalPercentage = percentage / 100;
    
        const amount = total * decimalPercentage;
    
        function bankersRound(num: number): number {
            let integer = Math.floor(num);
            let fraction = num - integer;
            
            if (fraction < 0.5) {
                return integer;
            } else if (fraction > 0.5) {
                return integer + 1;
            } else {
                return integer % 2 === 0 ? integer : integer + 1;
            }
        }
    
        return bankersRound(amount);
    }

    private static getMoneyFundPercentage(isAlreadyRetired: boolean, isTakingLumpSum: boolean, lumpSumHoldingFund: Fund): number {
        let percentage = 0;
    
        if (isAlreadyRetired) {
            return percentage;
        }
        
        if (!isTakingLumpSum) {
            return percentage;
        }
    
        const isHoldingLumpSumInMoneyFund = lumpSumHoldingFund.id == fundInfo.moneyFund.id;
    
        if (!isHoldingLumpSumInMoneyFund) {
            return percentage;
        }
    
        percentage += Constants.TaxFreeLumpSumPercentage;
        return percentage;
    }
}
